import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0502 from "../lesson-02/lesson";
import lesson0503 from "../lesson-03/lesson";
import lesson0221 from "../../course-02/lesson-21/lesson";

const linkLesson0219 = getLinkForCourseLesson(
  "4eb31243-b6cb-423f-919d-15bba103f7ce",
  "f95203e7-0946-4a0b-ba9e-28ecb546405c"
);
const linkLesson0221 = getLinkForCourseLesson("4eb31243-b6cb-423f-919d-15bba103f7ce", lesson0221.id);
const linkLesson0502 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0502.id);
const linkLesson0503 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0503.id);

/* eslint-disable */
const theory = `<h3 class=sectionHead>What will you learn in this lesson?</h3>
<p>This lesson is an introduction to the reverberation time as the most used parameter of room acoustics. It will explain how it is defined, how the optimum reverberation time is found, how it can be predicted and how it is measured.
<h3 class=sectionHead>Introduction</h3>
<p>
  As stated in previous chapters, a sound wave emitted by a source in a room propagates in all directions until it reaches a surface. Then it is partially reflected from the surface and partially absorbed by it. Considering the behaviour of sound in closed rooms in general, many reflections of the sound wave will occur in a room, as there will be a lot of reflections from the walls, floor, ceiling, and all objects in the room in a short time span. In mid-sized rooms, there will be hundreds of such occurrences per second.<br class=newline>When we observe how sound waves travel from a sound source to a receiver/listener in the room, there is more than one possible path to be found. The shortest and the quickest path is the direct path from the source to the listener’s ears (or a recording microphone). Apart from the direct path, there are many other paths that connect these two points. They can be deduced by considering one, two, or an arbitrary number of reflections from the surfaces of a room. This is explained and presented in <a href=${linkLesson0502}>Lesson 5.2</a>, where the impulse response of a room is presented as a direct consequence of the many paths the sound wave can take while propagating from the source to the receiver. A simple two-dimensional example of multiple paths between a sound source and a receiver is shown in <a href=# data-img-alt="Figure 1. Direct sound and reflections between a sound source and a sound receiver (left), and the matching Energy Time Curve (right)." data-img-src="${config.ASSETS_URL}/05/05/theory/figure-1.svg">Figure 1</a> (left). For an easier analysis of sound distribution over time, an Energy Time Curve is usually drawn, showing the amplitude of the direct sound and each individual reflection over time at a certain receiver position, as shown in <a href=# data-img-alt="Figure 1. Direct sound and reflections between a sound source and a sound receiver (left), and the matching Energy Time Curve (right)." data-img-src="${config.ASSETS_URL}/05/05/theory/figure-1.svg">Figure 1</a> (right). One obvious observation can be made – the reflections arriving later in time tend to have lower amplitudes than earlier reflections. There are two reasons for this. First, since most sound sources are point sources, the energy of the sound wave decreases with the increasing distance from the source. And second, the sound wave loses energy at each reflection, and this energy loss is proportional to the absorption coefficient of the reflecting surface.
  <figure class=figure>
<p><img alt="Figure 1. Direct sound and reflections between a sound source and a sound receiver (left), and the matching Energy Time Curve (right)." src="${config.ASSETS_URL}/05/05/theory/figure-1.svg"><figcaption class=caption><span class=id>Figure 1. </span><span class=content>Direct sound and reflections between a sound source and a sound receiver (left), and the matching Energy Time Curve (right).</span></figcaption></figure>
<h3 class=sectionHead>Diffuse sound field</h3>
<p>We will briefly revise the term diffuse sound field. Sound sources in rooms produce sound energy which can be presented as sound waves, or as sound rays, both of which propagate from the source, and are reflected from the room boundaries back into the room. Note that the representation of sound as rays is not a physical fact, but rather a way to model sound waves in spaces in a simplified way. The reflected sound fills the room, and after each reflection the sound field becomes more and more diffuse. Diffuse sound field is quite important for the simplification of sound propagation in a closed space.<br class=newline>A sound field can be considered diffuse if, statistically speaking, the same amount of sound energy arrives from each direction in each point of such a sound field in any given time. Since sound intensity takes into account not only the amount, but also the direction of the propagating sound by taking the product of sound pressure and particle velocity in any given point, and since all directions of propagation are equally probable, the total sound intensity in a perfectly diffuse sound field is zero. One can easily derive the expression for the average number of reflections per second from any surface S, if the room has a volume of V:<br class=newline>
<table class=equation>
  <tr>
    <td>
      <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
        <mover accent=true>
          <mrow>
            <mi>n</mi>
          </mrow>
          <mo accent=true>¯</mo>
        </mover>
        <mo class=MathClass-rel>=</mo>
        <mfrac>
          <mrow>
            <mi mathvariant=italic>cS</mi>
          </mrow>
          <mrow>
            <mn>4</mn>
            <mi>V</mi>
          </mrow>
        </mfrac>
      </math>
    <td class=eq-no>(1)
</table>
<h3 class=sectionHead>Reverberation time definition</h3>
<p>
  One of the very important room acoustic parameters, if not the most important one, is the so-called reverberation time. Here it will be explained how it is defined and how it relates to our subjective assessment of room acoustics.<br class=newline>To calculate the reverberation time, we must state some assumptions. We assume that there is a closed room of an arbitrary size with a sound source in it. It can be a loudspeaker, a musical instrument, a person speaking, singing, or clapping hands, etc. If this sound source emits sound energy and suddenly stops to do so (e.g. someone abruptly switches off the loudspeaker), the sound energy in the room will start to decay until the sound becomes imperceptible, usually after a short amount of time. Many people usually clap their hands when entering a big empty room or hall just to perceive how long the clap will be heard, thus making a conscious or a subconscious assessment of the reverberance of that room. Sometimes we do it for the sheer fun of it (especially children), but this is actually very close to how the reverberation time is defined and calculated.<br class=newline>Each surface in a room (walls, floor, ceiling, or objects in the room such as furniture, people, columns, partitions etc.) has an associated sound absorption coefficient, as defined in <a href=${linkLesson0503}>Lesson 5.3</a>. For the sake of simplicity, let us suppose that we have a simple room where all surfaces are made from the same material with the absorption coefficient
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mi>α</mi>
  </math>
  . Moreover, let us suppose that a sound source emits sound waves which can be thought of as sound rays, with a total sound energy
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <msub>
      <mrow>
        <mi>E</mi>
      </mrow>
      <mrow>
        <mn>0</mn>
      </mrow>
    </msub>
  </math>
  . When a sound ray hits any surface, it will be reflected with less energy, since a part of it will be absorbed. The remaining energy of the reflected ray equals to
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <msub>
      <mrow>
        <mi>E</mi>
      </mrow>
      <mrow>
        <mn>0</mn>
      </mrow>
    </msub>
  </math>
  (1-
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mi>α</mi>
  </math>
  ), as shown in <a href=# data-img-alt="Figure 2. Multiple reflections in a room for deriving the reverberation time." data-img-src="${config.ASSETS_URL}/05/05/theory/figure-2.svg">Figure 2</a>. After each subsequent reflection, the energy of the ray decreases by (1-
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mi>α</mi>
  </math>
  ). After N reflections, the energy of the ray becomes:
<table class=equation>
  <tr>
    <td style="font-size: 13px">
      <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
        <mo class=MathClass-open>⟨</mo>
        <msub>
          <mrow>
            <mi>E</mi>
          </mrow>
          <mrow>
            <mi>N</mi>
          </mrow>
        </msub>
        <mo class=MathClass-close>⟩</mo>
        <mo class=MathClass-rel>=</mo>
        <msub>
          <mrow>
            <mi>E</mi>
          </mrow>
          <mrow>
            <mn>0</mn>
          </mrow>
        </msub>
        <msup>
          <mrow>
            <mo class=MathClass-open>(</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <mover accent=true>
              <mrow>
                <mi>α</mi>
              </mrow>
              <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-close>)</mo>
          </mrow>
          <mrow>
            <mi>N</mi>
          </mrow>
        </msup>
        <mo class=MathClass-rel>=</mo>
        <msub>
          <mrow>
            <mi>E</mi>
          </mrow>
          <mrow>
            <mn>0</mn>
          </mrow>
        </msub>
        <mi class=MathClass-op>exp</mi>
        <mo>⁡</mo>
        <mo class=MathClass-open>[</mo>
        <mi mathvariant=italic>Nln</mi>
        <mo class=MathClass-open>(</mo>
        <mn>1</mn>
        <mo class=MathClass-bin>−</mo>
        <mover accent=true>
          <mrow>
            <mi>α</mi>
          </mrow>
          <mo accent=true>¯</mo>
        </mover>
        <mo class=MathClass-close>)</mo>
        <mo class=MathClass-close>]</mo>
      </math>
    <td class=eq-no>(2)
</table>
<figure class=figure>
  <p>
    <img alt="Figure 2. Multiple reflections in a room for deriving the reverberation time." src="${config.ASSETS_URL}/05/05/theory/figure-2.svg">
    <figcaption class=caption><span class=id>Figure 2. Multiple reflections in a room for deriving the reverberation time.</span></figcaption>
</figure>
<p>Expression (2) shows how the energy will drop after time t = 0, when the sound source was switched off and no new sound energy has been added to the room after that. If expression (1) is combined with (2), we will get a new expression that shows the dependency of the energy decrease on room surface, room volume and the absorption coefficient:
<table class=equation>
  <tr>
    <td style="font-size: 8px">
      <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
        <mi>E</mi>
        <mo class=MathClass-open>(</mo>
        <mi>t</mi>
        <mo class=MathClass-close>)</mo>
        <mo class=MathClass-rel>=</mo>
        <msub>
          <mrow>
            <mi>E</mi>
          </mrow>
          <mrow>
            <mn>0</mn>
          </mrow>
        </msub>
        <mi class=MathClass-op>exp</mi>
        <mo>⁡</mo>
        <mo class=MathClass-open>[</mo>
        <mfrac>
          <mrow>
            <mi mathvariant=italic>cS</mi>
          </mrow>
          <mrow>
            <mn>4</mn>
            <mi>V</mi>
          </mrow>
        </mfrac>
        <mi>t</mi>
        <mi class=MathClass-op>ln</mi>
        <mo>⁡</mo>
        <mo class=MathClass-open>(</mo>
        <mn>1</mn>
        <mo class=MathClass-bin>−</mo>
        <mover accent=true>
          <mrow>
            <mi>α</mi>
          </mrow>
          <mo accent=true>¯</mo>
        </mover>
        <mo class=MathClass-close>)</mo>
        <mo class=MathClass-close>]</mo>
        <mo class=MathClass-rel>=</mo>
        <msub>
          <mrow>
            <mi>E</mi>
          </mrow>
          <mrow>
            <mn>0</mn>
          </mrow>
        </msub>
        <mi class=MathClass-op>exp</mi>
        <mo>⁡</mo>
        <mo class=MathClass-open>[</mo>
        <mo class=MathClass-bin>−</mo>
        <mfrac>
          <mrow>
            <mi mathvariant=italic>cS</mi>
          </mrow>
          <mrow>
            <mn>4</mn>
            <mi>V</mi>
          </mrow>
        </mfrac>
        <mi mathvariant=italic>at</mi>
        <mo class=MathClass-close>]</mo>
        <mi mathvariant=italic>for</mi>
        <mi>t</mi>
        <mo class=MathClass-rel>≥</mo>
        <mn>0</mn>
      </math>
    <td class=eq-no>(3)
</table>
<p>where a is the absorption exponent defined as:
<table class=equation>
  <tr>
    <td>
      <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
        <mi>a</mi>
        <mo class=MathClass-rel>=</mo>
        <mo class=MathClass-bin>−</mo>
        <mi class=MathClass-op>ln</mi>
        <mo>⁡</mo>
        <mo class=MathClass-open>(</mo>
        <mn>1</mn>
        <mo class=MathClass-bin>−</mo>
        <mover accent=true>
          <mrow>
            <mi>α</mi>
          </mrow>
          <mo accent=true>¯</mo>
        </mover>
        <mo class=MathClass-close>)</mo>
      </math>
    <td class=eq-no>(4)
</table>
<p>The room parameter we call reverberation time is defined as the time required for the total sound energy to decrease by 60 dB from its initial value, and it is denoted with the letter T. A 60 dB decrease of the sound energy corresponds to one millionth of the initial energy magnitude. If we extract time from equation (3), we end up with the following expression:
<table class=equation>
  <tr>
    <td>
      <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
        <mi>T</mi>
        <mo class=MathClass-rel>=</mo>
        <mo class=MathClass-bin>−</mo>
        <mfrac>
          <mrow>
            <mn>24</mn>
            <mi>V</mi>
            <mi class=MathClass-op>ln</mi>
            <mo>⁡</mo>
            <mn>10</mn>
          </mrow>
          <mrow>
            <mi mathvariant=italic>cS</mi>
            <mi class=MathClass-op>ln</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <mover accent=true>
              <mrow>
                <mi>α</mi>
              </mrow>
              <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-close>)</mo>
          </mrow>
        </mfrac>
      </math>
    <td class=eq-no>(5)
</table>
<p>
  Two more steps are required to get to the well-known expression for calculating the reverberation time. One is to assume that the room temperature is 20<span class=tcrm-1000>°</span>C, which fixes the speed of sound to 343 m/s. This is a good assumption for most rooms in operating conditions throughout the year (with heating or cooling operational when needed). The second step is to include air attenuation in this calculation, due to the strong influence it has on reverberation time at high frequencies. This additional factor is represented with
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mi class=MathClass-op>exp</mi>
    <mo>⁡</mo>
    <mo class=MathClass-open>(</mo>
    <mo class=MathClass-bin>−</mo>
    <mi mathvariant=italic>mct</mi>
    <mo class=MathClass-close>)</mo>
  </math>
  that can be added to equation (3), where m is the attenuation constant of air. The resulting expression for reverberation time can now be written as:
<table class=equation>
  <tr>
    <td>
      <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
        <mi>T</mi>
        <mo class=MathClass-rel>=</mo>
        <mn>0</mn>
        <mo class=MathClass-punc>.</mo>
        <mn>16</mn>
        <mfrac>
          <mrow>
            <mi>V</mi>
          </mrow>
          <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi>S</mi>
            <mi class=MathClass-op>ln</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <mover accent=true>
              <mrow>
                <mi>α</mi>
              </mrow>
              <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mn>4</mn>
            <mi mathvariant=italic>mV</mi>
          </mrow>
        </mfrac>
      </math>
    <td class=eq-no>(6)
</table>
<p>
  Expression (5) is called the Eyring formula. In this expression,
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mi>α</mi>
  </math>
  is the average absorption coefficient calculated as the weighted sum of each surface area multiplied with its absorption coefficient:
<table class=equation>
  <tr>
    <td>
      <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
        <mover accent=true>
          <mrow>
            <mi>α</mi>
          </mrow>
          <mo accent=true>¯</mo>
        </mover>
        <mo class=MathClass-rel>=</mo>
        <mfrac>
          <mrow>
            <mn>1</mn>
          </mrow>
          <mrow>
            <mi>S</mi>
          </mrow>
        </mfrac>
        <munder class=msub>
          <mrow>
            <mo>∑</mo>
          </mrow>
          <mrow>
            <mi>i</mi>
          </mrow>
        </munder>
        <msub>
          <mrow>
            <mi>S</mi>
          </mrow>
          <mrow>
            <mi>i</mi>
          </mrow>
        </msub>
        <msub>
          <mrow>
            <mi>α</mi>
          </mrow>
          <mrow>
            <mi>i</mi>
          </mrow>
        </msub>
      </math>
    <td class=eq-no>(7)
</table>
<p>
  For small values of
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mi>α</mi>
  </math>
  (the limit value found in literature is usually
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mi>α</mi>
  </math>
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mo class=MathClass-rel><</mo>
  </math>
  0.2), which correspond to fairly reverberant rooms, this expression can be simplified to yield the well-known and famous Sabine formula:
<table class=equation>
  <tr>
    <td>
      <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
        <mi>T</mi>
        <mo class=MathClass-rel>=</mo>
        <mn>0</mn>
        <mo class=MathClass-punc>.</mo>
        <mn>16</mn>
        <mfrac>
          <mrow>
            <mi>V</mi>
          </mrow>
          <mrow>
            <mi mathvariant=italic>Sα</mi>
          </mrow>
        </mfrac>
        <mo class=MathClass-rel>=</mo>
        <mn>0</mn>
        <mo class=MathClass-punc>.</mo>
        <mn>16</mn>
        <mfrac>
          <mrow>
            <mi>V</mi>
          </mrow>
          <mrow>
            <mi>A</mi>
          </mrow>
        </mfrac>
      </math>
    <td class=eq-no>(8)
</table>
<p>
  Here a new term is introduced, the so-called equivalent (or total) absorption area, A [
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <msup>
      <mrow>
        <mi>m</mi>
      </mrow>
      <mrow>
        <mn>2</mn>
      </mrow>
    </msup>
  </math>
  ]:
<table class=equation>
  <tr>
    <td style="font-size: 12px">
      <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
        <mover accent=true>
          <mrow>
            <mi>α</mi>
          </mrow>
          <mo accent=true>¯</mo>
        </mover>
        <mo class=MathClass-rel>=</mo>
        <mfrac>
          <mrow>
            <mi>A</mi>
          </mrow>
          <mrow>
            <mi>S</mi>
          </mrow>
        </mfrac>
        <mo class=MathClass-rel>=</mo>
        <mfrac>
          <mrow>
            <mn>1</mn>
          </mrow>
          <mrow>
            <mi>S</mi>
          </mrow>
        </mfrac>
        <munder class=msub>
          <mrow>
            <mo>∑</mo>
          </mrow>
          <mrow>
            <mi>i</mi>
          </mrow>
        </munder>
        <msub>
          <mrow>
            <mi>S</mi>
          </mrow>
          <mrow>
            <mi>i</mi>
          </mrow>
        </msub>
        <msub>
          <mrow>
            <mi>α</mi>
          </mrow>
          <mrow>
            <mi>i</mi>
          </mrow>
        </msub>
        <mo class=MathClass-punc>;</mo>
        <mi>S</mi>
        <mo class=MathClass-rel>=</mo>
        <munder class=msub>
          <mrow>
            <mo>∑</mo>
          </mrow>
          <mrow>
            <mi>i</mi>
          </mrow>
        </munder>
        <msub>
          <mrow>
            <mi>S</mi>
          </mrow>
          <mrow>
            <mi>i</mi>
          </mrow>
        </msub>
        <mo class=MathClass-punc>;</mo>
        <mi>A</mi>
        <mo class=MathClass-rel>=</mo>
        <munder class=msub>
          <mrow>
            <mo>∑</mo>
          </mrow>
          <mrow>
            <mi>i</mi>
          </mrow>
        </munder>
        <msub>
          <mrow>
            <mi>S</mi>
          </mrow>
          <mrow>
            <mi>i</mi>
          </mrow>
        </msub>
        <msub>
          <mrow>
            <mi>α</mi>
          </mrow>
          <mrow>
            <mi>i</mi>
          </mrow>
        </msub>
      </math>
    <td class=eq-no>(9)
</table>
<p>
  To increase the equivalent absorption area, the absorption coefficient of one or more surfaces needs to be increased, with the physical properties of the room maintained. This means that using absorptive materials as surface finish in the room will increase the equivalent absorption area, and the reverberation time will decrease, as shown in <a href=# data-img-alt="Figure 3. Influence of the amount of absorptive materials in a room on the resulting reverberation time." data-img-src="${config.ASSETS_URL}/05/05/theory/figure-3.svg">Figure 3</a>. This is a crucial fact and the basis of modern room acoustic room design that strives to achieve the optimal reverberation time for a room depending on its purpose.
  <figure class=figure>
<p><img alt="Figure 3. Influence of the amount of absorptive materials in a room on the resulting reverberation time." src="${config.ASSETS_URL}/05/05/theory/figure-3.svg"><figcaption class=caption><span class=id>Figure 3.</span><span class=content>Influence of the amount of absorptive materials in a room on the resulting reverberation time.</span></figcaption></figure>
<p>
  Some building standards and recommendations are using the total absorption area as a parameter for of reverberation time optimization of spaces. For example, Document E of the Building Regulations of England (2015) recommends a minimum of 0.20
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <msup>
      <mrow>
        <mi>m</mi>
      </mrow>
      <mrow>
        <mn>2</mn>
      </mrow>
    </msup>
  </math>
  of total absorption area per cubic metre of volume for entrance halls, and a minimum of 0.25
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <msup>
      <mrow>
        <mi>m</mi>
      </mrow>
      <mrow>
        <mn>2</mn>
      </mrow>
    </msup>
  </math>
  total absorption area per cubic metre of volume for corridors or hallways. This means that if the ratio of total absorption area and the volume of a closed space is less that the above-mentioned recommended values, additional absorptive materials should be places on available surfaces, equally distributed, until this ratio exceeds the recommended values.
<h3 class=sectionHead>Optimum reverberation time</h3>
<p>
  Since the absorption coefficient is frequency dependent, the reverberation time shows the same tendency, as a direct consequence of equation (5). The subjective parameter that corresponds to reverberation time is reverberance, as defined in the standard ISO 3382, and it is highly correlated to reverberation time. We can estimate how long the reverberation time of a room is just by listening to sound decays of impulsive sounds. Although we do not have the ability to precisely estimate the reverberation time in different frequency regions, we can judge it by the change in timbre of the decaying sound. If the sound is “boomy” or “dull”, the room probably has longer reverberation time at low frequencies compared to the one at middle and high frequencies. If the decaying sound is “shrill” or “brilliant”, the room has long reverberation time at high frequencies.<br class=newline>If we measure the reverberation time, we might get a reverberation time curve similar to the one shown in <a href=# data-img-alt="Figure 4. Definition of the reverberation time (left) and an example of frequency dependent reverberation time curve (right) [1]." data-img-src="${config.ASSETS_URL}/05/05/theory/figure-4.svg">Figure 4</a> (left). <a href=# data-img-alt="Figure 4. Definition of the reverberation time (left) and an example of frequency dependent reverberation time curve (right) [1]." data-img-src="${config.ASSETS_URL}/05/05/theory/figure-4.svg">Figure 4</a> (right) shows how the reverberation time can vary over the frequency range of interest.
  <figure class=figure>
<p><img alt="Figure 4. Definition of the reverberation time (left) and an example of frequency dependent reverberation time curve (right) [1]." src="${config.ASSETS_URL}/05/05/theory/figure-4.svg"><figcaption class=caption><span class=id>Figure 4.</span><span class=content>Definition of the reverberation time (left) and an example of frequency dependent reverberation time curve (right) [1].</span></figcaption></figure>
<p>
  There are various recommendations in literature how the reverberation time can vary with frequency depending on the purpose of a room. As an example, orchestral music will tolerate (and demand) an increase of the reverberation time at low frequencies, whereas speech will benefit from a constant reverberation time over the entire frequency range, as shown in <a href=# data-img-alt="Figure 5. Recommended reverberation time characteristics for speech and music [2]." data-img-src="${config.ASSETS_URL}/05/05/theory/figure-5.svg">Figure 5</a>.
  <figure class=figure>
<p><img alt="Figure 5. Recommended reverberation time characteristics for speech and music [2]." src="${config.ASSETS_URL}/05/05/theory/figure-5.svg"><figcaption class=caption><span class=id>Figure 5.</span><span class=content>Recommended reverberation time characteristics for speech and music [2].</span></figcaption></figure>
<p>
  The optimum reverberation time must, therefore, be defined for certain frequency bands, or as a weighted single-number average of values in different frequency bands, if it is to be compared for different rooms. Moreover, it is clear from (5) that the reverberation time is proportional to room volume. This means that the optimum reverberation time cannot be uniquely defined for specific spaces such as lecture room or concert halls without taking into account their size. Therefore, various research groups and standardization committees have estimated the optimum reverberation time in a certain frequency band (e.g. for the octave band at 500 Hz) based on the purpose of a room and its volume, as shown in <a href=# data-img-alt="Figure 6. Optimal reverberation time for various purposes [1]." data-img-src="${config.ASSETS_URL}/05/05/theory/figure-6.svg">Figure 6</a>.
  <figure class=figure>
<p><img alt="Figure 6. Optimal reverberation time for various purposes [1]." src="${config.ASSETS_URL}/05/05/theory/figure-6.svg"><figcaption class=caption><span class=id>Figure 6. Optimal reverberation time for various purposes [1].</span></figcaption></figure>
<p>
  Another well-known standard that recommends optimum reverberation times is the DIN 18041 “Acoustic quality in small and middle-size rooms”. It was first published in 1968, and the latest version was issued in 2016. It gives recommendation for rooms of the following type: for music, for speech, for lecturing/communication (simple and inclusive), and for sports. These recommendations are dependent on room size, as shown in <a href=# data-img-alt="Figure 7. Optimal reverberation time according to DIN 18041." data-img-src="${config.ASSETS_URL}/05/05/theory/figure-7.svg">Figure 7</a>. The values of reverberation time that can be read from the chart are only default, reference values. Tolerance is also specified, as shown in <a href=# data-img-alt="Figure 8. Allowed tolerance of the frequency dependent reverberation time compared to the default, referent values at medium frequencies, according to DIN 18041." data-img-src="${config.ASSETS_URL}/05/05/theory/figure-8.svg">Figure 8</a>, and it allows a certain deviation from the optimum value, especially at low and high frequencies.
  <figure class=figure>
<p><img alt="Figure 7. Optimal reverberation time according to DIN 18041." src="${config.ASSETS_URL}/05/05/theory/figure-7.svg"><figcaption class=caption><span class=id>Figure 7. Optimal reverberation time according to DIN 18041.</span></figcaption></figure><figure class=figure><p><img alt="Figure 8. Allowed tolerance of the frequency dependent reverberation time compared to the default, referent values at medium frequencies, according to DIN 18041." src="${config.ASSETS_URL}/05/05/theory/figure-8.svg"><figcaption class=caption><span class=id>Figure 8. Allowed tolerance of the frequency dependent reverberation time compared to the default, referent values at medium frequencies, according to DIN 18041.</span></figcaption></figure>
<p>
  A good example of why it is important to define optimum reverberation time is intelligibility of speech which is key to human communication. Speech intelligibility is an important parameter that can be measured (see <a href=${linkLesson0219}>Lesson 2.19</a> and <a href=${linkLesson0221}>Lesson 2.21</a>), but it is also a direct consequence of reverberation time, as shown in <a href=# data-img-alt="Figure 9. Decline of speech intelligibility due to excessive reverberation time; example for the word “back” [3]." data-img-src="${config.ASSETS_URL}/05/05/theory/figure-9.svg">Figure 9</a>. Speech is a sound signal that varies a lot in amplitude with time, with most of the energy concentrated in vowels. If a vowel in a word excites a room that has excessive reverberation time, the reverberation will mask subsequent vowels or consonants, thus making the word (or words) incomprehensible. Therefore, rooms where speech must be intelligible (such as lecture rooms of any kind, theatres, worship spaces etc.) must have limited reverberation time, to be acoustically comfortable for the users.
  <figure class=figure>
<p><img alt="Figure 9. Decline of speech intelligibility due to excessive reverberation time; example for the word “back” [3]." src="${config.ASSETS_URL}/05/05/theory/figure-9.svg"><figcaption class=caption><span class=id>Figure 9. Decline of speech intelligibility due to excessive reverberation time; example for the word “back” [3].</span></figcaption></figure>
<h3 class=sectionHead>Prediction of reverberation time</h3>
<p>
  Reverberation time can be predicted in the simplest way by using equation (7). However, as stated before, there is a strong assumption hidden behind this equation - that the sound field in the room is diffuse. This is technically true in certain cases, but for many real rooms there is a strong deviation from this idealized condition. This happens mostly because of the shape of the room. For example, rectangular rooms have a strong modal behaviour, thus influencing the reverberation time at low frequencies. Long rooms such as corridors do not display diffuse field conditions. The second most common reason for not having a diffuse field in the room is the amount and the placement of sound absorbing materials. If the materials are evenly distributed on all or most of the room surfaces, a sufficiently diffuse field can be achieved. On the other hand, an uneven distribution of sound absorbing materials will result in an irregular sound field. In such a case, the reverberation time (and other acoustic parameters) will greatly depend on the position inside the room.<br class=newline>This effect can be easily explained on the example of an opera house. The sound in the audience seating area can be perceived as very dry due to lot of absorptive materials in the opera house and the scenography. The sound in the orchestra pit is much more reverberant and live, as there are many reflective surfaces in the pit that were put there by design, in order for the musicians to be able to hear themselves well. An uneven distribution of acoustically absorptive materials on room surfaces demands the prediction of reverberation time to be done with much more precision and reliability. To achieve this, room acoustics simulation software based on ray-tracing algorithms should be used instead of Sabine or Eyring formulas.<br class=newline>A simple case of calculating reverberation time in a music rehearsal room is shown below. The surface area of all surfaces if the room must be determined, and the values of the absorption coefficient of the materials placed on these surfaces have to be known. This room has a floor area of 10.7
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <msup>
      <mrow>
        <mi>m</mi>
      </mrow>
      <mrow>
        <mn>2</mn>
      </mrow>
    </msup>
  </math>
  , and a volume of 29.4
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <msup>
      <mrow>
        <mi>m</mi>
      </mrow>
      <mrow>
        <mn>3</mn>
      </mrow>
    </msup>
  </math>
  . The goal was to get a room with a reverberation time of 0.6 s at middle frequencies. With this information and the known room volume, we can calculate the reverberation time using the standard equation (7), as shown in <a href=# data-img-alt="Figure 10. An example of reverberation time calculation for a small lecture room." data-img-src="${config.ASSETS_URL}/05/05/theory/figure-10.svg">Figure 10</a>, and the resulting values are shown in <a href=# data-img-alt="Figure 11. Graph of the calculated, frequency dependent reverberation time values." data-img-src="${config.ASSETS_URL}/05/05/theory/figure-11.svg">Figure 11</a>.
  <figure class=figure>
<p><img alt="Figure 10. An example of reverberation time calculation for a small lecture room." src="${config.ASSETS_URL}/05/05/theory/figure-10.svg"><figcaption class=caption><span class=id>Figure 10. An example of reverberation time calculation for a small lecture room.</span></figcaption></figure><figure class=figure><p><img alt="Figure 11. Graph of the calculated, frequency dependent reverberation time values." src="${config.ASSETS_URL}/05/05/theory/figure-11.svg"><figcaption class=caption><span class=id>Figure 11. Graph of the calculated, frequency dependent reverberation time values.</span></figcaption></figure>
<h3 class=sectionHead>Measurement of reverberation time</h3>
<p>
  Reverberation time is one of the most commonly measured room acoustic parameters since it is very intuitive to understand and tells us a lot about the acoustics of the room. The simplest, yet a very imprecise way to measure it is to excite the room with an impulsive sound such as handclap, footstep, balloon pop, explosion of a firecracker, etc. The response of the room to such a sound can be listened to, and the time can be measured from the instance of the original sound to the instance when the reverberation cannot be heard anymore. This is how the reverberation time equation was originally derived by Sabine in the late 19th century – he used his own ears as microphones, a stopwatch for measuring the time, and some organ pipes as the sound source.<br class=newline>In modern times, the measurement of the reverberation time is done mostly by following the procedure described in the ISO 3382 standard, as this ensures the comparability and the repeatability of measurements. The common procedure for all described methods is to excite the room by a sound source, either continuous or impulsive, and to measure and evaluate the decay of sound energy after the sound source has been switched off.<br class=newline>The norm prescribes the following measurement methods: the interrupted noise method and the integrated impulse response method. The latter is nowadays widely preferred due to good repeatability and the possibility to increase the dynamics of the measured impulse response by using averaging on multiple measurements for the same sound source-microphone combination. The impulse response must be integrated to derive a decay curve which is used for determining the reverberation time, as shown in <a href=# data-img-alt="Figure 12. Reverberation time measurement setup." data-img-src="${config.ASSETS_URL}/05/05/theory/figure-12.svg">Figure 12</a>.<br class=newline>Special care is taken to choose a proper sound source, regarding its directional properties. The source is usually a specially designed loudspeaker that meets the criteria on directivity stipulated in ISO 3382. Such loudspeakers are usually built as multiple-driver sphere-imitating enclosures (dodecahedrons or cubes). For smaller rooms, the source design can have a loudspeaker hidden inside the enclosure with a small opening, so that the opening behaves as a point source at all frequencies of interest. The standard frequency range of interest includes octave bands from 125 Hz to 4 kHz, or 1/3-octave bands from 100 Hz to 5 000 Hz. The extended frequency range includes the octave bands at 63 Hz and 8 kHz as well.
  <figure class=figure>
<p><img alt="Figure 12. Reverberation time measurement setup." src="${config.ASSETS_URL}/05/05/theory/figure-12.svg"><figcaption class=caption><span class=id>Figure 12. Reverberation time measurement setup.</span></figcaption></figure>
<p>Since it is very hard to achieve a signal to noise ratio of over 60 dB in order to measure the reverberation time according to its definition (especially at low frequencies), the reverberation time is often measured as the time when the sound energy decreases only 30 dB or even 20 dB, and the measured value is multiplied by 2 or 3 in order to receive value the reverberation time parameter. We mark. One important assumption here is that the sound energy decay curve is linear, as shown in <a href=# data-img-alt="Figure 4. Definition of the reverberation time (left) and an example of frequency dependent reverberation time curve (right) [1]." data-img-src="${config.ASSETS_URL}/05/05/theory/figure-4.svg">Figure 4</a>. Usually, we measure or calculate reverberation time values in octave, or one-third octave frequency bands. To differentiate the values of the reverberation time measured as a decay of 60, 30 or 20 dB, we use the following labels:<br class=newline>
<ul class=itemize1>
  <li class=itemize>T60 – the time the sound pressure level takes to decrease from 5 dB to 65 dB below its initial level,
  <li class=itemize>T30 – the time the sound pressure level takes to decrease from 5 dB to 35 dB below its initial level,
  <li class=itemize>T20 – the time the sound pressure level takes to decrease from 5 dB to 25 dB below its initial level.
</ul>
<h3 class=likesectionHead>References</h3>

<dl>
  <dt>[1]</dt><dd>Multibook of Architectural Acoustics, https://arac-multibook.com, 2018</dd>
  <dt>[2]</dt><dd>Michael Barron, Auditorium Acoustics and Architectural Design (Second Edition), Spon Press, London / New York, 2009</dd>
  <dt>[3]</dt><dd>F. Alton Everest and Ken C. Pohlmann, Master Handbook of Acoustics (Fifith Edition), McGraw Hill, New York, 2009</dd>
</dl>`;
/* eslint-enable */

export default theory;
